import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  FiArrowLeft,
  FiArrowRight,
  FiCheck,
  FiFileText,
  FiKey,
  FiLock,
  FiMail,
  FiMessageCircle,
  FiShield,
  FiUser,
} from 'react-icons/fi';
import { SmokeBackground } from '../../components/ui/spooky-smoke-animation';
import { usePortal } from '../../context/PortalContext';

const formatCPF = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
};

const formatCNPJ = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 14);
  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  if (digits.length <= 8) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
  if (digits.length <= 12) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
};

const formatDocumento = (value) => {
  const digits = value.replace(/\D/g, '');
  return digits.length <= 11 ? formatCPF(value) : formatCNPJ(value);
};

const portalBenefits = [
  'Consulte seus planos ativos',
  'Acompanhe faturas e vencimentos',
  'Acesse pagamentos em poucos cliques',
];

const LoginPortal = () => {
  const [activeTab, setActiveTab] = useState('fatura');
  const [documento, setDocumento] = useState('');
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const { loginComDocumento, loading, error, setError, isLoggedIn } = usePortal();
  const navigate = useNavigate();

  const digitCount = documento.replace(/\D/g, '').length;
  const isCNPJ = digitCount > 11;

  useEffect(() => {
    if (isLoggedIn) navigate('/portal/dashboard', { replace: true });
  }, [isLoggedIn, navigate]);

  const changeTab = (tab) => {
    setActiveTab(tab);
    setError(null);
  };

  const handleDocumentoChange = (event) => {
    setDocumento(formatDocumento(event.target.value));
    setError(null);
  };

  const handleLogin = async (event) => {
    event.preventDefault();

    if (!isCNPJ && digitCount < 11) {
      setError('Digite um CPF válido com 11 dígitos.');
      return;
    }

    if (isCNPJ && digitCount < 14) {
      setError('Digite um CNPJ válido com 14 dígitos.');
      return;
    }

    const authenticated = await loginComDocumento(documento);
    if (authenticated) navigate('/portal/dashboard');
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#0a0a0a] text-white">
      <div className="pointer-events-none fixed inset-0 z-0">
        <SmokeBackground smokeColor="#303030" />
        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_75%_35%,rgba(255,255,255,0.09),transparent_34%)]" />
      </div>

      <header className="absolute inset-x-0 top-0 z-20">
        <div className="mx-auto flex w-full max-w-7xl items-center justify-between px-5 py-6 sm:px-8 lg:px-10">
          <Link to="/" className="flex items-center gap-3" aria-label="Voltar à página inicial">
            <span className="font-display text-2xl font-bold tracking-tighter text-white">AyTech</span>
            <span className="hidden border-l border-white/20 pl-3 text-xs uppercase tracking-[0.2em] text-white/55 sm:inline">
              Portal do cliente
            </span>
          </Link>

          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-medium text-white/65 transition-colors hover:text-white"
          >
            <FiArrowLeft className="h-4 w-4" />
            Voltar ao site
          </Link>
        </div>
      </header>

      <main className="relative z-10 mx-auto grid min-h-screen w-full max-w-7xl items-center gap-12 px-5 pb-12 pt-28 sm:px-8 lg:grid-cols-[minmax(0,1fr)_minmax(420px,0.78fr)] lg:gap-20 lg:px-10 lg:pt-24">
        <motion.section
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.75, ease: [0.2, 0.8, 0.2, 1] }}
          className="max-w-2xl"
        >
          <span className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.06] px-4 py-2 text-xs font-medium uppercase tracking-[0.18em] text-white/70 backdrop-blur-xl">
            <FiShield className="h-4 w-4" />
            Ambiente seguro
          </span>

          <h1 className="max-w-xl font-display text-4xl font-bold leading-[1.02] tracking-tighter text-white sm:text-5xl lg:text-6xl">
            Sua operação AyTech em um só lugar.
          </h1>

          <p className="mt-6 max-w-xl text-base leading-relaxed text-white/65 sm:text-lg">
            Consulte planos, acompanhe vencimentos e mantenha os serviços da sua empresa sempre em dia.
          </p>

          <div className="mt-10 grid gap-3 sm:grid-cols-3 lg:max-w-2xl">
            {portalBenefits.map((benefit) => (
              <div
                key={benefit}
                className="flex items-start gap-2.5 border-t border-white/15 pt-4 text-sm leading-relaxed text-white/70"
              >
                <FiCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-white" />
                {benefit}
              </div>
            ))}
          </div>

          <div className="mt-10 flex items-center gap-3 text-sm text-white/45">
            <FiLock className="h-4 w-4" />
            Seus dados são usados somente para localizar sua assinatura.
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 28, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.12, ease: [0.2, 0.8, 0.2, 1] }}
          className="w-full overflow-hidden rounded-3xl border border-white/20 bg-white/[0.10] shadow-2xl shadow-black/35 backdrop-blur-2xl"
        >
          <div className="grid grid-cols-2 border-b border-white/15 bg-black/10 p-1.5">
            <button
              type="button"
              onClick={() => changeTab('fatura')}
              className={`relative rounded-2xl px-4 py-3 text-sm font-semibold transition-colors ${
                activeTab === 'fatura' ? 'text-gray-950' : 'text-white/55 hover:text-white'
              }`}
            >
              {activeTab === 'fatura' && (
                <motion.span
                  layoutId="portal-tab"
                  className="absolute inset-0 rounded-2xl bg-white"
                  transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                />
              )}
              <span className="relative z-10">Fatura fácil</span>
            </button>

            <button
              type="button"
              onClick={() => changeTab('assinante')}
              className={`relative rounded-2xl px-4 py-3 text-sm font-semibold transition-colors ${
                activeTab === 'assinante' ? 'text-gray-950' : 'text-white/55 hover:text-white'
              }`}
            >
              {activeTab === 'assinante' && (
                <motion.span
                  layoutId="portal-tab"
                  className="absolute inset-0 rounded-2xl bg-white"
                  transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                />
              )}
              <span className="relative z-10">Área do assinante</span>
            </button>
          </div>

          <div className="p-6 sm:p-8">
            <AnimatePresence mode="wait">
              {activeTab === 'fatura' ? (
                <motion.div
                  key="fatura"
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 16 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="mb-7 flex items-start gap-4">
                    <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl border border-white/15 bg-white/10">
                      <FiFileText className="h-5 w-5" />
                    </div>
                    <div>
                      <h2 className="font-display text-2xl font-bold tracking-tight">Acesse sua fatura</h2>
                      <p className="mt-1 text-sm leading-relaxed text-white/55">
                        Informe o CPF ou CNPJ vinculado ao seu contrato.
                      </p>
                    </div>
                  </div>

                  <form onSubmit={handleLogin} className="space-y-4">
                    <div>
                      <label htmlFor="documento-input" className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-white/50">
                        CPF ou CNPJ
                      </label>
                      <div className="relative">
                        <FiUser className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-white/35" />
                        <input
                          id="documento-input"
                          type="text"
                          inputMode="numeric"
                          placeholder={isCNPJ ? '00.000.000/0000-00' : '000.000.000-00'}
                          value={documento}
                          onChange={handleDocumentoChange}
                          autoComplete="off"
                          className={`w-full rounded-2xl border bg-black/20 py-3.5 pl-11 pr-4 text-white outline-none transition placeholder:text-white/25 ${
                            error
                              ? 'border-red-400/70 focus:border-red-300'
                              : 'border-white/15 focus:border-white/45 focus:bg-black/30'
                          }`}
                        />
                      </div>

                      <div className="mt-2 min-h-[20px]">
                        <AnimatePresence mode="wait">
                          {error ? (
                            <motion.p
                              key="error"
                              initial={{ opacity: 0, y: -4 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0 }}
                              className="text-sm text-red-300"
                            >
                              {error}
                            </motion.p>
                          ) : digitCount > 0 ? (
                            <motion.p
                              key="document-type"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              className="text-xs text-white/40"
                            >
                              {isCNPJ ? 'CNPJ identificado' : 'CPF identificado'}
                            </motion.p>
                          ) : null}
                        </AnimatePresence>
                      </div>
                    </div>

                    <motion.button
                      type="submit"
                      disabled={loading}
                      whileHover={loading ? undefined : { scale: 1.01 }}
                      whileTap={loading ? undefined : { scale: 0.99 }}
                      className="flex w-full items-center justify-center gap-2 rounded-2xl bg-white px-5 py-3.5 text-sm font-bold text-gray-950 transition hover:bg-white/90 disabled:cursor-wait disabled:opacity-65"
                    >
                      {loading ? (
                        <>
                          <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-400 border-t-gray-950" />
                          Verificando acesso
                        </>
                      ) : (
                        <>
                          Continuar
                          <FiArrowRight className="h-4 w-4" />
                        </>
                      )}
                    </motion.button>
                  </form>
                </motion.div>
              ) : (
                <motion.div
                  key="assinante"
                  initial={{ opacity: 0, x: 16 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -16 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="mb-7 flex items-start gap-4">
                    <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl border border-white/15 bg-white/10">
                      <FiKey className="h-5 w-5" />
                    </div>
                    <div>
                      <h2 className="font-display text-2xl font-bold tracking-tight">Área do assinante</h2>
                      <p className="mt-1 text-sm leading-relaxed text-white/55">
                        O acesso com e-mail e senha estará disponível em breve.
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="relative">
                      <FiMail className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-white/30" />
                      <input
                        type="email"
                        placeholder="seu@email.com"
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                        className="w-full rounded-2xl border border-white/10 bg-black/15 py-3.5 pl-11 pr-4 text-white/60 outline-none placeholder:text-white/25"
                      />
                    </div>

                    <div className="relative">
                      <FiLock className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-white/30" />
                      <input
                        type="password"
                        placeholder="Senha"
                        value={senha}
                        onChange={(event) => setSenha(event.target.value)}
                        className="w-full rounded-2xl border border-white/10 bg-black/15 py-3.5 pl-11 pr-4 text-white/60 outline-none placeholder:text-white/25"
                      />
                    </div>

                    <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-4 text-sm leading-relaxed text-white/55">
                      Enquanto isso, use a aba <strong className="text-white">Fatura fácil</strong> para entrar com seu CPF ou CNPJ.
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="flex flex-col gap-3 border-t border-white/10 px-6 py-5 text-xs text-white/40 sm:flex-row sm:items-center sm:justify-between sm:px-8">
            <span className="inline-flex items-center gap-2">
              <FiLock className="h-3.5 w-3.5" />
              Conexão protegida
            </span>
            <a
              href="https://wa.me/5541987718296"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 transition-colors hover:text-white"
            >
              <FiMessageCircle className="h-3.5 w-3.5" />
              Precisa de ajuda?
            </a>
          </div>
        </motion.section>
      </main>

      <footer className="relative z-10 pb-6 text-center text-xs text-white/30 lg:absolute lg:bottom-0 lg:left-0 lg:right-0">
        © 2026 AyTech Soluções Tecnológicas
      </footer>
    </div>
  );
};

export default LoginPortal;
